"use client";

import React from 'react';
import { X, Check, Scale, Sparkles } from 'lucide-react';
import { useTranslations } from 'next-intl';

const ComparisonSection: React.FC = () => {
  const t = useTranslations();

  const comparisons = [
    {
      feature: t('comp_feature_1'),
      traditional: t('comp_trad_feature_1'),
      ours: t('comp_ours_feature_1'),
      highlight: true
    },
    {
      feature: t('comp_feature_2'),
      traditional: t('comp_trad_feature_2'),
      ours: t('comp_ours_feature_2'),
      highlight: true
    },
    {
      feature: t('comp_feature_3'),
      traditional: t('comp_trad_feature_3'),
      ours: t('comp_ours_feature_3'),
      highlight: false
    },
    {
      feature: t('comp_feature_4'),
      traditional: t('comp_trad_feature_4'),
      ours: t('comp_ours_feature_4'),
      highlight: false
    },
    {
      feature: t('comp_feature_5'),
      traditional: t('comp_trad_feature_5'),
      ours: t('comp_ours_feature_5'),
      highlight: false
    },
    {
      feature: t('comp_feature_6'),
      traditional: t('comp_trad_feature_6'),
      ours: t('comp_ours_feature_6'),
      highlight: true
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-slate-950 relative overflow-hidden" id="comparison">
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-slate-950 to-slate-950"></div>

      <div className="container-std relative z-10">
        {/* Header */}
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary mb-6">
            <Scale size={14} />
            <span className="text-xs font-bold uppercase tracking-wider">
              {t('comparison_badge')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-foreground mb-6">
            {t('comparison_title_main')}
          </h2>
          <p className="text-base md:text-lg text-muted-foreground">
            {t('comparison_desc')}
          </p>
        </div>

        {/* Comparison — Desktop: 3-column table / Mobile: cards */}
        <div className="max-w-4xl mx-auto">

          {/* === DESKTOP TABLE (md+) === */}
          <div className="hidden md:block glass-card rounded-3xl border border-white/10 overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-3 gap-4 p-6 bg-white/5 border-b border-white/10">
              <div className="text-muted-foreground font-medium">
                {t('comparison_criteria')}
              </div>
              <div className="text-center text-slate-400 font-medium">
                {t('comparison_traditional_path')}
              </div>
              <div className="text-center text-primary font-medium">
                {t('comparison_with_kinofolio')}
              </div>
            </div>
            {/* Rows */}
            {comparisons.map((row, idx) => (
              <div
                key={idx}
                className={`grid grid-cols-3 gap-4 p-6 border-b border-white/5 last:border-0 ${row.highlight ? 'bg-primary/5' : ''}`}
              >
                <div className="flex items-center text-foreground font-medium text-sm">
                  {row.feature}
                </div>
                <div className="flex items-center justify-center gap-2 text-slate-400">
                  <X size={16} className="text-red-400 shrink-0" />
                  <span className="text-sm">{row.traditional}</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-green-400 font-medium">
                  <Check size={16} className="shrink-0" />
                  <span className="text-sm">{row.ours}</span>
                </div>
              </div>
            ))}
          </div>

          {/* === MOBILE CARDS (< md) === */}
          <div className="md:hidden space-y-3">
            {comparisons.map((row, idx) => (
              <div
                key={idx}
                className={`rounded-2xl border overflow-hidden ${row.highlight ? 'border-primary/25' : 'border-white/8'}`}
              >
                {/* Feature label */}
                <div className={`px-4 py-3 text-xs font-bold text-foreground/70 uppercase tracking-wider ${row.highlight ? 'bg-primary/10' : 'bg-white/4'}`}>
                  {row.feature}
                </div>
                {/* Traditional */}
                <div className="flex items-center gap-3 px-4 py-3 border-t border-white/6">
                  <X size={14} className="text-red-400 shrink-0" />
                  <span className="text-sm text-slate-400 leading-snug">{row.traditional}</span>
                </div>
                {/* Ours */}
                <div className={`flex items-center gap-3 px-4 py-3 border-t border-white/6 ${row.highlight ? 'bg-primary/5' : 'bg-green-500/3'}`}>
                  <Check size={14} className="text-green-400 shrink-0" />
                  <span className="text-sm text-green-400 font-semibold leading-snug">{row.ours}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div className="mt-8 p-4 md:p-6 rounded-2xl bg-yellow-500/10 border border-yellow-500/20">
            <div className="flex items-start gap-3 md:gap-4">
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center text-yellow-400 shrink-0">
                <Sparkles size={18} />
              </div>
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm md:text-base">
                  {t('comparison_disclaimer_title')}
                </h3>
                <p className="text-muted-foreground text-xs md:text-sm leading-relaxed">
                  {t('comparison_disclaimer_text')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
